import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Modal, Input, Button, Popconfirm, message } from "antd";
import { Trash, PencilSimple, Sparkle } from "@phosphor-icons/react";
import {
  fetchCollection,
  renameCollection,
  deleteCollection,
  removeCollectionDocuments,
  prepareCollection,
} from "../../service/documentCollections";

/**
 * The manage surface a collection never had: `renameCollection`,
 * `deleteCollection`, and `removeCollectionDocuments` have existed on the
 * API since collections shipped, with no caller. Opened by clicking a row
 * in `MatterCollectionsCard`.
 */

const STATE_LABEL = {
  ready: "Ready",
  partial: "Partially ready",
  preparing: "Preparing…",
  not_ready: "Not prepared",
  empty: "Empty",
};

const stateStyle = (state) => {
  switch (state) {
    case "ready":
      return { color: "#177a3e", bg: "#e7f6ec" };
    case "failed":
      return { color: "#b42318", bg: "#fee4e2" };
    case "not_ready":
      return { color: "#6b7280", bg: "#f3f4f6" };
    default:
      // Any in-progress state the backend reports (queued, extracting, …).
      return { color: "#92400e", bg: "#fffbeb" };
  }
};

const Summary = styled.div`
  margin-bottom: 12px;
  color: #5a6c87;
  font-size: 13px;
`;

const DocList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const DocRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f6;

  &:last-child {
    border-bottom: none;
  }
`;

const DocName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #1f2937;
`;

const StateBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  color: ${(p) => p.$color || "#4b5563"};
  background: ${(p) => p.$bg || "#f3f4f6"};
`;

const TitleRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const EditIcon = styled(PencilSimple)`
  cursor: pointer;
  color: #8a93a3;

  &:hover {
    color: #4b5563;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

const CollectionDetailModal = ({ open, collectionId, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [collection, setCollection] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    try {
      const data = await fetchCollection(collectionId);
      setCollection(data);
      setNameDraft(data?.name || "");
    } catch (err) {
      message.error("Couldn't load this collection.");
      setCollection(null);
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  useEffect(() => {
    if (open) {
      setEditingName(false);
      load();
    }
  }, [open, load]);

  const handleRename = useCallback(async () => {
    if (renaming) return;
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === collection?.name) {
      setEditingName(false);
      return;
    }
    setRenaming(true);
    try {
      const saved = await renameCollection(collectionId, { name: trimmed });
      setCollection((prev) => ({ ...prev, ...saved }));
      setEditingName(false);
      onChanged && onChanged();
    } catch (err) {
      message.error("Couldn't rename — try again.");
    } finally {
      setRenaming(false);
    }
  }, [renaming, nameDraft, collection, collectionId, onChanged]);

  const handlePrepare = useCallback(async () => {
    if (!collectionId) return;
    setPreparing(true);
    try {
      await prepareCollection(collectionId);
      message.success("Preparing this collection for AI chat.");
      await load();
    } catch (err) {
      message.error("Couldn't start AI preparation — try again.");
    } finally {
      setPreparing(false);
    }
  }, [collectionId, load]);

  const handleRemoveDoc = useCallback(
    async (documentId) => {
      if (!collectionId) return;
      setRemovingId(documentId);
      try {
        await removeCollectionDocuments(collectionId, [documentId]);
        await load();
        onChanged && onChanged();
      } catch (err) {
        message.error("Couldn't remove that document — try again.");
      } finally {
        setRemovingId(null);
      }
    },
    [collectionId, load, onChanged]
  );

  const handleDelete = useCallback(async () => {
    if (!collectionId) return;
    setDeleting(true);
    try {
      await deleteCollection(collectionId);
      message.success(`Deleted "${collection?.name || "collection"}".`);
      onChanged && onChanged();
      onClose();
    } catch (err) {
      message.error("Couldn't delete the collection — try again.");
      setDeleting(false);
    }
  }, [collectionId, collection, onChanged, onClose]);

  const readiness = collection?.readiness;
  const documents = readiness?.documents || [];

  return (
    <Modal
      title={
        editingName ? (
          <Input
            autoFocus
            value={nameDraft}
            disabled={renaming}
            onChange={(e) => setNameDraft(e.target.value)}
            onPressEnter={handleRename}
            onBlur={handleRename}
            maxLength={200}
            style={{ maxWidth: 360 }}
            data-testid="collection-rename-input"
          />
        ) : (
          <TitleRow>
            {collection?.name || "Collection"}
            <EditIcon
              size={14}
              onClick={() => setEditingName(true)}
              data-testid="collection-rename-trigger"
            />
          </TitleRow>
        )
      }
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={560}
    >
      {loading ? (
        <Summary>Loading…</Summary>
      ) : !collection ? (
        <Summary>Couldn&apos;t load this collection.</Summary>
      ) : (
        <>
          <Summary>
            {readiness ? (
              <>
                {STATE_LABEL[readiness.state] || readiness.state} ·{" "}
                {readiness.ready || 0} of {readiness.total || 0} ready for AI
                chat
              </>
            ) : (
              `${collection.member_count} document(s)`
            )}
          </Summary>

          <DocList data-testid="collection-document-rows">
            {documents.length === 0 ? (
              <DocRow>
                <DocName>No documents in this collection yet.</DocName>
              </DocRow>
            ) : (
              documents.map((doc) => {
                const style = stateStyle(doc.state);
                return (
                  <DocRow key={doc.document_id}>
                    <DocName title={doc.name}>{doc.name}</DocName>
                    <StateBadge $color={style.color} $bg={style.bg}>
                      {doc.state}
                    </StateBadge>
                    <Button
                      type="text"
                      size="small"
                      danger
                      loading={removingId === doc.document_id}
                      onClick={() => handleRemoveDoc(doc.document_id)}
                      icon={<Trash size={14} />}
                      aria-label={`Remove ${doc.name}`}
                    />
                  </DocRow>
                );
              })
            )}
          </DocList>

          <Actions>
            <Popconfirm
              title="Delete this collection?"
              description="Documents stay in the matter — only the group is removed."
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleDelete}
            >
              <Button danger icon={<Trash size={14} />}>
                Delete collection
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<Sparkle size={14} weight="fill" />}
              loading={preparing}
              disabled={!documents.length}
              onClick={handlePrepare}
            >
              Prepare for AI
            </Button>
          </Actions>
        </>
      )}
    </Modal>
  );
};

CollectionDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  collectionId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

export default CollectionDetailModal;
